import type { QueryClient } from '@tanstack/react-query';
import { redirect, type LoaderFunction, type RouteObject } from 'react-router';
import { requireMe } from '../session/loaders';
import { homeHref } from '../session/presenter';

/** A route that draws a page; one without is a redirect, which works the same in a preview. */
function drawsPage(route: RouteObject): boolean {
  return route.Component != null || route.element != null || route.lazy !== undefined;
}

/**
 * Wraps every page's loader so that, while previewing, it fetches nothing (TEST1, APP-059). The
 * shell shows the page's sample layout instead (`SamplePage`), so no live record is requested and
 * a page the person couldn't open themselves never turns into an error. Outside a preview the
 * loaders run exactly as written. Redirect-only routes keep their loaders. Home keeps its one
 * rule: Payroll's Home is its queue, for a previewed Payroll too.
 */
export function previewAware(
  routes: readonly RouteObject[],
  queryClient: QueryClient,
): RouteObject[] {
  return routes.map((route): RouteObject => {
    const loader = route.loader;
    const wrapped: LoaderFunction | undefined =
      typeof loader === 'function' && drawsPage(route)
        ? async (args, context) => {
            const me = await requireMe(queryClient, args.request);
            if (me.preview === null) return loader(args, context);
            const home = homeHref(me);
            // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router redirect.
            if (route.index === true && home !== '/') throw redirect(home);
            return null;
          }
        : undefined;
    if (route.index === true) return wrapped === undefined ? route : { ...route, loader: wrapped };
    const children =
      route.children === undefined ? undefined : previewAware(route.children, queryClient);
    return {
      ...route,
      ...(wrapped !== undefined && { loader: wrapped }),
      ...(children !== undefined && { children }),
    };
  });
}
